import { RadioTower } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { useFlightAwareEnabled, useSetFlightAwareEnabled } from '@/hooks/useFlightAware'

interface FlightAwareLocationCardProps {
  locationId: string | null
}

/**
 * Per-location enable/disable for FlightAware. FlightAware uses a shared
 * platform API key (no per-org credentials), so it isn't set up through the
 * Connection Wizard — this toggle creates/activates the location credential
 * directly. Syncing itself is manual, from the Today board.
 */
export function FlightAwareLocationCard({ locationId }: FlightAwareLocationCardProps) {
  const enabled = useFlightAwareEnabled(locationId)
  const setEnabled = useSetFlightAwareEnabled()

  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <RadioTower className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-semibold">FlightAware AeroAPI</span>
              <Badge variant="secondary" className="text-[10px]">Manual sync</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 ml-6 max-w-md">
              Pull airport arrivals &amp; departures to enrich schedule ETAs and import new flights.
              Run it from the <span className="font-medium">Sync FlightAware</span> button on the Today board.
            </p>
          </div>

          <div className="shrink-0">
            <Switch
              checked={enabled}
              disabled={!locationId || setEnabled.isPending}
              onCheckedChange={(checked) =>
                locationId && setEnabled.mutate({ locationId, enabled: checked })
              }
            />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
